import { Key } from "react";

import axios from "axios";

import ROUTES, { API_URL } from "shared/config/apiRoutes";
import type {
  DealLumenRunSummary,
  Vero1ChecklistPayload,
  Vero1Checklist,
  Vero1ChecklistItem,
  Vero1ChecklistVersionSummary,
  Vero1CustomItemInput,
  Vero1ItemEditRequest,
  Vero1SubmitInput,
} from "shared/interfaces/api/vero1";
import { transformRoute } from "shared/utils/routing";

const url = (route: string, id: Key, params: Record<string, Key> = {}) =>
  `${API_URL}${transformRoute(route, { id, ...params })}`;

export const getVero1Checklist = async (
  dealId: Key
): Promise<Vero1ChecklistPayload> =>
  (await axios.get(url(ROUTES.vero1Checklist, dealId))).data;

export interface Vero1ReportFieldsPatch {
  final_recommendation?: string | null;
  final_recommendation_label?: string;
  cancel_reason?: string;
  instruction_line?: string;
  decision_rationale?: string;
  client_findings?: string;
  /** Sent by Copy over: the previous review the values came from. */
  copied_from_version?: number;
}

export const editVero1ChecklistReport = async (
  dealId: Key,
  fields: Vero1ReportFieldsPatch
): Promise<{ updatedAt: string }> =>
  (await axios.patch(url(ROUTES.vero1ChecklistReport, dealId), fields)).data;

export const startVero1Checklist = async (
  dealId: Key
): Promise<Vero1Checklist> =>
  (await axios.post(url(ROUTES.vero1ChecklistStart, dealId))).data;

export const refreshVero1Checklist = async (
  dealId: Key
): Promise<Vero1Checklist> =>
  (await axios.post(url(ROUTES.vero1ChecklistRefresh, dealId))).data;

export const carryForwardVero1Checklist = async (
  dealId: Key
): Promise<Vero1Checklist> =>
  (await axios.post(url(ROUTES.vero1ChecklistCarryForward, dealId))).data;

export const addVero1ChecklistItem = async (
  dealId: Key,
  item: Vero1CustomItemInput
): Promise<
  Vero1Checklist & { addedItemId?: Key; templateStatus?: string | null }
> => (await axios.post(url(ROUTES.vero1ChecklistItems, dealId), item)).data;

export const editVero1ChecklistItem = async (
  dealId: Key,
  itemId: Key,
  edit: Vero1ItemEditRequest
): Promise<Vero1Checklist> =>
  (await axios.patch(url(ROUTES.vero1ChecklistItem, dealId, { itemId }), edit))
    .data;

/** Autosave path: only the written row and the fresh token come back. */
export const editVero1ChecklistItemSlim = async (
  dealId: Key,
  itemId: Key,
  edit: Vero1ItemEditRequest
): Promise<{ item: Vero1ChecklistItem; updatedAt: string }> =>
  (
    await axios.patch(
      `${url(ROUTES.vero1ChecklistItem, dealId, { itemId })}?response=item`,
      edit
    )
  ).data;

export const deleteVero1ChecklistItem = async (
  dealId: Key,
  itemId: Key
): Promise<Vero1Checklist> =>
  (await axios.delete(url(ROUTES.vero1ChecklistItem, dealId, { itemId }))).data;

export const submitVero1Checklist = async (
  dealId: Key,
  payload: Vero1SubmitInput
): Promise<Vero1Checklist> =>
  (await axios.post(url(ROUTES.vero1ChecklistSubmit, dealId), payload)).data;

// deliberately returns nothing: the endpoint echoes the DISCARDED checklist,
// and seeding the cache with it would show a discarded review instead of
// refetching into the ready-to-start state
export const discardVero1Checklist = async (dealId: Key): Promise<void> => {
  await axios.post(url(ROUTES.vero1ChecklistDiscard, dealId));
};

export const getVero1ChecklistVersions = async (
  dealId: Key
): Promise<Vero1ChecklistVersionSummary[]> =>
  (await axios.get(url(ROUTES.vero1ChecklistVersions, dealId))).data;

export const getVero1ChecklistVersion = async (
  dealId: Key,
  version: number
): Promise<Vero1Checklist> =>
  (await axios.get(url(ROUTES.vero1ChecklistVersion, dealId, { version })))
    .data;

export const getDealLumenRuns = async (
  dealId: Key
): Promise<DealLumenRunSummary[]> =>
  (await axios.get(url(ROUTES.dealLumenRuns, dealId))).data;
